import React from "react";
import { Box, BoxProps } from "@UI/Components/Box/Box";
import { Text } from "@UI/Components/Text/Text";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { Button } from "@UI/Components/Button/Button";
import { Image } from "@UI/Components/Image/Image";
import { RouterLink } from "@Libs/Router/Components/RouterLink/RouterLink";
import useI18n from "@I18n/hooks/useI18n";
import { useRecommendations } from "../../hooks/useRecommendations";

const ctaIllustrations: Record<string, string> = {
  "fr-FR": require("assets/images/recommendations/cta_illustration/fr-FR.png"),
  "fr-BE": require("assets/images/recommendations/cta_illustration/fr-BE.png"),
  "nl-BE": require("assets/images/recommendations/cta_illustration/nl-BE.png"),
};

type Props = BoxProps;

export const CreateRequestCTA = (props: Props) => {
  const { locale, formatNumber } = useI18n();
  const illustrationSrc = ctaIllustrations[locale] ?? ctaIllustrations["fr-FR"];
  const { serviceProvidersCount, placeName } = useRecommendations();

  return (
    <Box
      backgroundColor="sand.100"
      borderRadius="3xl"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      textAlign="center"
      marginBottom={8}
      {...props}
    >
      {/* Top section: stat + illustration, with radial centered on the illustration */}
      <Box
        position="relative"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        alignItems="center"
        paddingX={{ base: 4, md: 8 }}
        paddingTop={{ base: 8, md: 10 }}
        paddingBottom={4}
      >
        {!!serviceProvidersCount && placeName && (
          <Text
            fontSize="sm"
            color="gray.600"
            position="relative"
            zIndex={1}
            marginBottom={2}
          >
            <FormattedMessage
              id="recommendations.create_request_cta.talent_count"
              defaultMessage="{count} trusted talents in {placeName}"
              values={{ count: formatNumber(serviceProvidersCount), placeName }}
            />
          </Text>
        )}

        {/* Rings are nested inside the illustration box so they're always centered on it */}
        <Box position="relative" zIndex={1} width="100%">
          <img
            src={require("assets/images/recommendations/cta_illustration/concentric_rings.svg")}
            aria-hidden="true"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              // rings center is at (50%, 40.3%) within the SVG viewBox (172/345, 145/360)
              transform: "translate(-50%, -40.3%)",
              width: "100%",
              height: "auto",
              display: "block",
              pointerEvents: "none",
            }}
          />
          <Image
            src={illustrationSrc}
            alt="Illustration showing avatars and a map on a phone"
            width="100%"
            height="auto"
            objectFit="contain"
            position="relative"
          />
        </Box>
      </Box>

      {/* Bottom section: title + subtitle + CTA */}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        paddingX={3}
        paddingY={8}
        marginTop={-8}
        gap={2}
        zIndex={1}
        background="linear-gradient(180deg, rgba(253, 248, 244, 0.1) -2.05%, #FDF8F4 19.01%)"
      >
        <Text as="h3" fontSize="md" color="gray.800" fontWeight="bold">
          <FormattedMessage
            id="recommendations.create_request_cta.title"
            defaultMessage="Whatever you need, someone can help."
          />
        </Text>

        <Text fontSize="sm" color="gray.600">
          <FormattedMessage
            id="recommendations.create_request_cta.subtitle"
            defaultMessage="Create your request and compare proposals."
          />
        </Text>

        <Button
          as={RouterLink}
          route="REQUEST_SERVICE"
          query={{ from: "create_request_cta" }}
          reloadDocument
          variant="solid"
          colorScheme="seaGreen"
          size="lg"
          borderRadius="xl"
          marginTop={2}
        >
          <FormattedMessage
            id="recommendations.create_request_cta.button"
            defaultMessage="Create a request"
          />
        </Button>
      </Box>
    </Box>
  );
};
